/**
 * Breath content — source of truth for the Breath practices.
 *
 * IMAGES — each practice owns a unique editorial photograph. All URLs
 * live in `lib/editorialImages.js`; do not hard-code them here.
 */

import { EDITORIAL } from "@/lib/editorialImages";

export const BREATH_INTENTIONS = [
  "I receive.",
  "I release.",
  "I soften.",
  "I listen.",
  "I return.",
  "I trust.",
  "I am here.",
];

export const BREATH_PRACTICES = [
  {
    slug: "conscious-breathing",
    title: "Conscious Breathing",
    subtitle: "Awareness & Presence",
    tagline: "The simplest doorway. Notice the breath that is already here.",
    image: EDITORIAL.breathConscious,
    thePractice:
      "The most foundational of all breath practices. You simply breathe — slowly and deliberately — and notice.",
    why:
      "Awareness is the doorway. Every deeper breath practice begins here. Before we change anything, we simply arrive.",
    how: [
      "Sit or stand comfortably. Let your shoulders soften.",
      "Breathe in gently through the nose.",
      "Breathe out slowly. Let it be a little longer than the inhale, if that comes naturally.",
      "That's it. Do this for as long as you'd like.",
    ],
    when:
      "Anytime. Especially as a doorway to any of the other practices, or as a small reset in the middle of an ordinary day.",
    notice: [
      "The subtle shift when the mind arrives with the body.",
      "Where the breath naturally lives — chest, ribs, belly.",
      "What softens without you asking it to.",
    ],
    reflection: "What became quieter as I began to notice?",
    reminder:
      "There is nothing to force. You are only noticing the breath that has been with you all along.",
    phases: [
      { name: "Inhale", seconds: 4, scale: 1 },
      { name: "Exhale", seconds: 6, scale: 0.55 },
    ],
    recommendedMinutes: [2, 5],
  },
  {
    slug: "extended-exhale",
    title: "Extended Exhale",
    subtitle: "Release & Softening",
    tagline: "Let the exhale be longer than the inhale, and let the body follow.",
    image: EDITORIAL.breathExtendedExhale,
    thePractice:
      "You lengthen the exhale so that it is longer than the inhale. That is the entire practice.",
    why:
      "A longer exhale is one of the body's clearest signals of safety. It quietly tells the nervous system that it is okay to soften. Over a few breaths, tension often begins to leave the shoulders and jaw.",
    how: [
      "Breathe in through the nose for a count of four.",
      "Exhale slowly through the nose or lightly parted lips for a count of six — or eight, if it comes easily.",
      "Repeat. Do not force the length; simply invite it.",
    ],
    when:
      "When you are wound up, wired, anxious, or bracing. Before sleep. After a difficult moment. Any time you notice your shoulders creeping up.",
    notice: [
      "The place tension has been living without your awareness.",
      "The small drop in the body between one exhale and the next inhale.",
      "The room around you becoming a little quieter, without changing at all.",
    ],
    reflection: "Where did I soften first?",
    reminder:
      "The exhale is not something you push out. It is something you allow.",
    phases: [
      { name: "Inhale", seconds: 4, scale: 1 },
      { name: "Exhale", seconds: 8, scale: 0.5 },
    ],
    recommendedMinutes: [2, 5],
  },
  {
    slug: "box-breathing",
    title: "Box Breathing",
    subtitle: "Focus & Steadiness",
    tagline: "Four sides of equal length. The breath of composure.",
    image: EDITORIAL.breathBox,
    thePractice:
      "You inhale, hold, exhale, and hold — all for equal counts. Four sides of the same length. A square you draw with your breath.",
    why:
      "Structure creates steadiness. When the world feels chaotic or the mind is racing, an ordered breath gives the nervous system something to hold. It is the breath used by people who need composure under pressure.",
    how: [
      "Inhale gently through the nose for a count of four.",
      "Hold, softly, for a count of four.",
      "Exhale slowly for a count of four.",
      "Hold again, softly, for a count of four. Repeat.",
    ],
    when:
      "Before something that asks for your composure. During overwhelm. When you need to bring the mind back to one clear line.",
    notice: [
      "How the pauses feel between motions — the quiet corners of the square.",
      "The way the mind organizes itself around the count.",
      "Whether one of the four sides feels more natural than the others.",
    ],
    reflection: "What does steadiness feel like in my body right now?",
    reminder:
      "The holds are gentle — never strain. If four counts feel long, start with three.",
    phases: [
      { name: "Inhale", seconds: 4, scale: 1 },
      { name: "Hold", seconds: 4, scale: 1 },
      { name: "Exhale", seconds: 4, scale: 0.5 },
      { name: "Hold", seconds: 4, scale: 0.5 },
    ],
    recommendedMinutes: [2, 5],
  },
  {
    slug: "physiological-sigh",
    title: "Physiological Sigh",
    subtitle: "Reset & Release",
    tagline: "A single breath that can change the shape of a moment.",
    image: EDITORIAL.breathPhysiological,
    thePractice:
      "Two inhales through the nose — the second smaller than the first — followed by one long, slow exhale through the mouth. Repeat once or twice. That is all.",
    why:
      "This is one of the body's fastest, most natural ways of releasing accumulated tension. It doesn't require minutes. Even a single sigh can shift the state you were bracing against.",
    how: [
      "Inhale through the nose — a full, slow breath.",
      "Without exhaling, take a second smaller sip of air through the nose.",
      "Then release a long, slow exhale through the mouth.",
      "You can repeat one or two more times if it feels right. No more is needed.",
    ],
    when:
      "When you notice you have been holding your breath. When emotion is close. When you need to reset in under a minute.",
    notice: [
      "The very small drop in the shoulders after the long exhale.",
      "The way the mouth softens on the way out.",
      "How little effort this actually takes.",
    ],
    reflection: "What am I ready to release, even just a little?",
    reminder:
      "This is not about doing it many times. Even one is enough.",
    phases: [
      { name: "Inhale", seconds: 3, scale: 0.95 },
      { name: "Top-up", seconds: 1, scale: 1 },
      { name: "Long exhale", seconds: 6, scale: 0.5 },
      { name: "Rest", seconds: 2, scale: 0.6 },
    ],
    recommendedMinutes: [1, 3],
  },
  {
    slug: "coherent-breathing",
    title: "Coherent Breathing",
    subtitle: "Rhythm & Balance",
    tagline: "A steady rhythm the heart, breath, and body can share.",
    image: EDITORIAL.breathCoherent,
    thePractice:
      "A slow, even rhythm — inhale for five, exhale for five. No holds. You continue the rhythm for several minutes, until the whole system quiets around it.",
    why:
      "This rhythm — roughly six breaths per minute — is where the breath, the heart, and the nervous system begin to move in quiet harmony. It doesn't take much to feel it. It takes a little time.",
    how: [
      "Sit comfortably. Let your face and jaw soften.",
      "Breathe in through the nose for a count of five.",
      "Breathe out through the nose for a count of five.",
      "No pauses at the top or bottom. Just a steady, even rhythm.",
      "Continue for several minutes. Let the count fade into the background.",
    ],
    when:
      "When you have a few minutes to arrive. Before rest, meditation, or a difficult conversation. As a slower, longer practice.",
    notice: [
      "What happens to the mind when the breath finds its rhythm.",
      "Whether the rhythm eventually seems to breathe you.",
      "How the room feels different after just a few minutes.",
    ],
    reflection: "What arrived when I stopped trying?",
    reminder:
      "The rhythm is the medicine. There is nowhere to rush to.",
    phases: [
      { name: "Inhale", seconds: 5, scale: 1 },
      { name: "Exhale", seconds: 5, scale: 0.55 },
    ],
    recommendedMinutes: [3, 5, 10],
  },
];

export function findBreathPractice(slug) {
  return BREATH_PRACTICES.find((p) => p.slug === slug) || null;
}
